import React, { useState, useMemo } from 'react';
import { cn } from '../../lib/utils';

export interface TailwindDataGridColumn {
  field: string;
  headerName: string;
  width?: number;
  minWidth?: number;
  flex?: number;
  align?: 'left' | 'center' | 'right';
  headerAlign?: 'left' | 'center' | 'right';
  sortable?: boolean;
  renderCell?: (params: { row: any; value: any; field: string }) => React.ReactNode;
  renderHeader?: (params: { field: string; headerName: string }) => React.ReactNode;
  valueGetter?: (params: { row: any }) => any;
  type?: 'string' | 'number' | 'date' | 'boolean';
}

export interface TailwindDataGridProps {
  rows: any[];
  columns: TailwindDataGridColumn[];
  getRowId?: (row: any) => string | number;
  density?: 'compact' | 'standard' | 'comfortable';
  pagination?: boolean;
  pageSize?: number;
  autoPageSize?: boolean;
  className?: string;
  onRowClick?: (row: any) => void;
  // Master-detail expansion
  getDetailPanelContent?: (params: { row: any }) => React.ReactNode;
  getDetailPanelHeight?: (params: { row: any }) => number | 'auto';
  // Slots
  slots?: {
    detailPanelExpandIcon?: React.ComponentType;
    detailPanelCollapseIcon?: React.ComponentType;
  };
}

export default function TailwindDataGrid({
  rows,
  columns,
  getRowId = (row) => row.id,
  density = 'standard',
  pagination = false,
  pageSize = 10,
  autoPageSize = false,
  className,
  onRowClick,
  getDetailPanelContent,
  getDetailPanelHeight,
  slots,
}: TailwindDataGridProps) {
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());

  // Density classes
  const densityClasses = {
    compact: 'py-1 px-2 text-xs',
    standard: 'py-2 px-3 text-sm',
    comfortable: 'py-3 px-4 text-sm',
  };

  const headerDensityClasses = {
    compact: 'py-1.5 px-2 text-xs',
    standard: 'py-2.5 px-3 text-sm',
    comfortable: 'py-3.5 px-4 text-sm',
  };

  // Sorting logic
  const sortedRows = useMemo(() => {
    if (!sortField) return rows;

    const column = columns.find(col => col.field === sortField);

    return [...rows].sort((a, b) => {
      let aValue = column?.valueGetter ? column.valueGetter({ row: a }) : a[sortField];
      let bValue = column?.valueGetter ? column.valueGetter({ row: b }) : b[sortField];

      // Handle null/undefined
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Type-specific comparison
      if (column?.type === 'number') {
        aValue = Number(aValue);
        bValue = Number(bValue);
      } else if (column?.type === 'date') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else {
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }

      const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [rows, sortField, sortDirection, columns]);

  // Pagination logic
  const paginatedRows = useMemo(() => {
    if (!pagination) return sortedRows;
    const start = currentPage * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, pagination, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedRows.length / pageSize);

  // Handle sort
  const handleSort = (field: string) => {
    const column = columns.find(col => col.field === field);
    if (!column?.sortable) return;

    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (rowId: string | number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  // Get cell value
  const getCellValue = (row: any, column: TailwindDataGridColumn) => {
    if (column.valueGetter) {
      return column.valueGetter({ row });
    }
    return row[column.field];
  };

  // Get alignment classes
  const getAlignClass = (align?: 'left' | 'center' | 'right') => {
    if (align === 'center') return 'text-center justify-center';
    if (align === 'right') return 'text-right justify-end';
    return 'text-left justify-start';
  };

  const ExpandIcon = slots?.detailPanelExpandIcon;
  const CollapseIcon = slots?.detailPanelCollapseIcon;

  return (
    <div className={cn('flex flex-col w-full h-full', className)}>
      {/* Table container */}
      <div className="flex-1 overflow-auto border border-slate-200 rounded-lg">
        <table className="w-full border-collapse">
          {/* Header */}
          <thead className="bg-slate-50 sticky top-0 z-10">
            <tr>
              {/* Expansion column */}
              {getDetailPanelContent && (
                <th className={cn(
                  'border-b border-slate-200 font-semibold text-slate-700',
                  headerDensityClasses[density],
                  'w-12'
                )}>
                  {/* Empty header for expand column */}
                </th>
              )}

              {columns.map((column) => (
                <th
                  key={column.field}
                  className={cn(
                    'border-b border-slate-200 font-semibold text-slate-700',
                    headerDensityClasses[density],
                    getAlignClass(column.headerAlign || column.align),
                    column.sortable && 'cursor-pointer hover:bg-slate-100 select-none'
                  )}
                  style={{
                    width: column.width,
                    minWidth: column.minWidth,
                    flex: column.flex,
                  }}
                  onClick={() => column.sortable && handleSort(column.field)}
                >
                  <div className="flex items-center gap-1">
                    {column.renderHeader
                      ? column.renderHeader({ field: column.field, headerName: column.headerName })
                      : <span>{column.headerName}</span>}
                    {column.sortable && sortField === column.field && (
                      <span className="material-symbols-outlined text-[16px]">
                        {sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {paginatedRows.map((row, rowIndex) => {
              const rowId = getRowId(row);
              const isExpanded = expandedRows.has(rowId);

              return (
                <React.Fragment key={rowId}>
                  {/* Main row */}
                  <tr
                    className={cn(
                      'border-b border-slate-100 transition-colors',
                      onRowClick && 'cursor-pointer hover:bg-slate-50',
                      rowIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                    )}
                    onClick={() => onRowClick?.(row)}
                  >
                    {/* Expansion cell */}
                    {getDetailPanelContent && (
                      <td className={cn(densityClasses[density], 'border-r border-slate-100')}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowExpansion(rowId);
                          }}
                          className="p-1 hover:bg-slate-200 rounded transition-colors"
                        >
                          {isExpanded ? (
                            CollapseIcon ? <CollapseIcon /> : <span className="material-symbols-outlined text-[20px]">expand_less</span>
                          ) : (
                            ExpandIcon ? <ExpandIcon /> : <span className="material-symbols-outlined text-[20px]">expand_more</span>
                          )}
                        </button>
                      </td>
                    )}

                    {columns.map((column) => {
                      const value = getCellValue(row, column);

                      return (
                        <td
                          key={column.field}
                          className={cn(
                            densityClasses[density],
                            getAlignClass(column.align),
                            'text-slate-900'
                          )}
                          style={{
                            width: column.width,
                            minWidth: column.minWidth,
                            flex: column.flex,
                          }}
                        >
                          {column.renderCell ? (
                            column.renderCell({ row, value, field: column.field })
                          ) : (
                            <span>{value != null ? String(value) : ''}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Detail panel row */}
                  {getDetailPanelContent && isExpanded && (
                    <tr>
                      <td colSpan={columns.length + 1} className="bg-slate-50 p-4 border-b border-slate-200">
                        {getDetailPanelContent({ row })}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {/* Empty state */}
            {paginatedRows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length + (getDetailPanelContent ? 1 : 0)}
                  className="py-12 text-center text-slate-500"
                >
                  No rows to display
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-white">
          <div className="text-sm text-slate-600">
            Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, sortedRows.length)} of {sortedRows.length} rows
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              className={cn(
                'px-3 py-1.5 text-sm rounded border transition-colors',
                currentPage === 0
                  ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                  : 'border-slate-300 text-slate-700 hover:bg-slate-50'
              )}
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              className={cn(
                'px-3 py-1.5 text-sm rounded border transition-colors',
                currentPage === 0
                  ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                  : 'border-slate-300 text-slate-700 hover:bg-slate-50'
              )}
            >
              Previous
            </button>
            <span className="text-sm text-slate-600">
              Page {currentPage + 1} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
              disabled={currentPage >= totalPages - 1}
              className={cn(
                'px-3 py-1.5 text-sm rounded border transition-colors',
                currentPage >= totalPages - 1
                  ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                  : 'border-slate-300 text-slate-700 hover:bg-slate-50'
              )}
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
              className={cn(
                'px-3 py-1.5 text-sm rounded border transition-colors',
                currentPage >= totalPages - 1
                  ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                  : 'border-slate-300 text-slate-700 hover:bg-slate-50'
              )}
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
